/// A wrapper around JQuery autocomplete. We could possibly replace
/// with the MUI v5 version but the v4 -> v5 upgrade takes some
/// work and not all areas of the UI are React yet so there would
/// be some inconsistency in controls.

import * as React from "react";
import { useEffect, useState } from "react";

export interface AutoCompleteRequest {
  term: string;
}

export interface AutoCompleteOption {
  label: string;
  value: string;
}

interface AutoCompleteProps {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  minCharsToSearch?: number;
  inputIdName: string;
  inputNameName: string;
  selectedOption?: AutoCompleteOption;
  source: (
    request: AutoCompleteRequest,
    response: (data: AutoCompleteOption[]) => void,
  ) => void | AutoCompleteOption[] | string[];
  onOptionSelected?: (option?: AutoCompleteOption) => void;
}

export const AutoComplete = (props: AutoCompleteProps): JSX.Element => {
  const {
    label,
    placeholder,
    disabled,
    minCharsToSearch,
    inputIdName,
    inputNameName,
    source,
  } = { ...props };
  const [selectedOption, setSelectedOption] = useState(props.selectedOption);
  const [hiddenElement, setHiddenElement] = useState<HTMLInputElement>();
  const [inputElement, setInputElement] = useState<HTMLInputElement>();

  useEffect(() => {
    if (inputElement) {
      if (selectedOption) {
        inputElement.value = selectedOption.label;
      }
      $(inputElement).autocomplete({
        source: source,
        position: {
          my: "left top",
          at: "left bottom",
          collision: "flip", // JQuery's default is "none" here
        },
        select: (
          event: JQueryEventObject,
          ui: JQueryUI.AutocompleteUIParams,
        ) => {
          event.preventDefault();
          setSelectedOption(ui.item);
        },
        change: (
          event: JQueryEventObject,
          ui: JQueryUI.AutocompleteUIParams,
        ) => {
          event.preventDefault();
          setSelectedOption(ui.item);
        },
        minLength: minCharsToSearch ?? 0,
        delay: 500,
      });
      return () => {
        $(inputElement).autocomplete("destroy");
      };
    }
  }, [inputElement]);

  useEffect(() => {
    if (hiddenElement && selectedOption) {
      hiddenElement.value = selectedOption.value;
    }
  }, [hiddenElement]);

  useEffect(() => {
    // Only change the text of the input if the selection changed.
    // If no option was selected we don't want to override what the
    // user has typed.
    if (inputElement && selectedOption) {
      inputElement.value = selectedOption.label;
    }
    if (hiddenElement) {
      hiddenElement.value = selectedOption?.value ?? "";
    }
    if (
      props.onOptionSelected &&
      selectedOption?.value !== props.selectedOption?.value
    ) {
      props.onOptionSelected(selectedOption);
    }
  }, [selectedOption]);

  useEffect(() => {
    setSelectedOption(props.selectedOption);
    // If the component was reinitialized then clear out the user's input
    // otherwise it _looks_ like something is selected even though it isn't.
    if (inputElement) {
      inputElement.value = props.selectedOption
        ? props.selectedOption.label
        : "";
    }
  }, [props.selectedOption]);

  return (
    <>
      <label
        className="text-display-normal text-color-secondary"
        htmlFor={inputNameName}
      >
        {label}
      </label>
      <input
        ref={(node) => setHiddenElement(node ?? undefined)}
        type="hidden"
        name={inputIdName}
        disabled={disabled}
      ></input>
      <input
        ref={(node) => setInputElement(node ?? undefined)}
        className="form-control"
        id={inputNameName}
        name={inputNameName}
        disabled={disabled}
        placeholder={placeholder}
        onChange={() => {
          if (inputElement?.value !== selectedOption?.value) {
            setSelectedOption(undefined);
          }
        }}
        onFocus={() => inputElement && $(inputElement).autocomplete("search")}
      ></input>
    </>
  );
};
